import type { Route, RouteKind } from './router';

/**
 * THE NAVIGATION MODEL — and the rule it exists to enforce.
 *
 * ⚠ ONE ENTRY PER SECTION THAT EXISTS. No entry for a section a later wave will build,
 * no disabled tab, no «в разработке» tile, no placeholder card. A control a subscriber
 * can see and cannot use is the defect class this project has paid for repeatedly
 * (D109's dead controls; the three admin lists that cost a whole fix pass), and on a
 * subscriber-facing screen it is worse than on a staff one: he cannot ask a colleague
 * what the greyed-out item means, so he phones the operator.
 *
 * W1b shipped two entries (identity only). W2 added six — the money sections, read-only.
 * W3 added two: the sections that TAKE MONEY. **W4 adds four: the SELF-SERVICE sections**,
 * each with the screen behind it in this same wave — and each with its word deleted from
 * `nav.test.tsx`'s `UNBUILT_VOCABULARY` in the same edit, because the wave that makes a
 * forbidden word legitimate is the wave that must delete it.
 *
 *   home         — «Мои аккаунты»: balance, availability, the accounts, THE SUSPENSION
 *                  NOTICE (Appendix L §3)
 *   topup        — «Оплата»: the card form, SBP, the saved card and the auto-payment
 *                  switch (§8b, §8d, §10, §10a) — W3
 *   instructions — «Другие способы оплаты»: how to pay in the office or at a terminal,
 *                  as sanitized Markdown (§8e) — W3
 *   payments     — «История платежей»: the payment history with its corrections (§22)
 *   usage        — «Расход средств»: what the money went on, per month (§9)
 *   state        — «Состояние счёта»: the balance and the month's two totals (§6)
 *   documents    — «Ежемесячный счёт»: the portal-visible documents and their bytes (§19)
 *   installments — «Рассрочка»: the schedules, view-only (§20)
 *   promises     — «Обещанные платежи»: the terms, the request FORM and the history
 *                  (§21 + §23) — the form is W3's
 *   profile      — «Профиль»: the contacts and their CONFIRMATION (§4, F4) — W4
 *   blockings    — «Блокировки»: «отпуск» — block, unblock, cancel a plan, history
 *                  (§13A, F19/F21) — W4
 *   devices      — «Мои устройства»: the registered Devices and their de-registration
 *                  (§24, F23) — W4
 *   accountPassword — «Пароль учётной записи»: the ENROLLMENT credential (§11, F22) — W4
 *   password     — change the portal password (§5)
 *
 * ⚠ ~~THE ORDER IS BY EXPECTED CALL VOLUME, not by the spec's menu tree. The spec draws a
 * grouped left sidebar («Оплата», «Счёт», «Интернет»); this portal has a single horizontal
 * strip, because at 360 px a collapsing sidebar is a drawer nobody opens. With no groups to
 * hang them on, the sections are ordered the way Appendix L's own support volume suggests —
 * «what do I owe», «how do I pay it», «did my payment arrive» — which is also the order
 * P7-S3 prescribes for the phase as a whole, there being no legacy parity target. W3 puts
 * «Оплата» SECOND for that reason and for one more: the reader this portal must not fail is
 * a suspended subscriber inside the walled garden, and the section that ends his suspension
 * cannot be at the far end of a strip he has to scroll.~~
 *
 * **⚠ SUPERSEDED 2026-08-14 — THE FLAT STRIP IS GONE AND `NAV_ENTRIES`' ORDER NO LONGER
 * DECIDES WHAT THE SUBSCRIBER SEES FIRST.** The customer reported the strip as a defect: at
 * twenty-one entries it produced a HORIZONTAL SCROLLBAR ACROSS THE TOP OF EVERY PAGE at every
 * viewport width, phone and desktop alike, and the sections past «Обещанные платежи» were
 * behind a sideways gesture nobody makes. The paragraph above is kept rather than deleted
 * because its reasoning is still the reasoning — «a suspended subscriber must not have to
 * scroll to the section that ends his suspension» — and the rework is what finally HONOURS it:
 * nothing is behind a scroll now, because nothing scrolls.
 *
 * What replaced it: **`NAV_GROUPS` below — one landing link and five named groups**, each
 * opening a short list, rendered as a WRAPPING bar (`global.css`, `.pa-nav-bar`). The order of
 * `NAV_ENTRIES` survives as the order WITHIN a group (it is filtered, never re-sorted), so the
 * expected-call-volume argument still decides which row of «Платежи» a subscriber reads first;
 * what it no longer decides is which sections he can see at all without a gesture.
 *
 * ⚠ AND THE GROUPS ARE THE SPEC'S OWN, not invented here. `ui-enduser-personal-area`'s
 * «Navigation subtree» draws exactly this tree — Payment (Оплата) / Invoice (Счёт) / Internet
 * (Интернет) / Technical support / Messages / Change password — and it is the tree the
 * operator's EXISTING subscribers have used for years, which at cutover is the whole estate.
 * Three deliberate departures from it, each because a group must not be a word the subscriber
 * also reads as a section:
 *
 *   «Платежи» and not «Оплата» — «Оплата» is a LEAF here (the card form), and a parent whose
 *     name equals its own child is a menu a subscriber reads twice. The word «Платежи» was
 *     freed for exactly this when W3 renamed the history «История платежей».
 *   «Услуги» merges the spec's «Интернет» and «Телевидение» — this installation sells one
 *     access service and one television service, and two groups of four and one would be a
 *     group of one, which is a menu item wearing a hat.
 *   «Помощь» and not «Техническая поддержка» — same collision (§26's section is «Поддержка»),
 *     and it is six characters instead of twenty-one on a 360-px row.
 *
 * `nav.test.tsx` pins the no-collision rule mechanically, so a later label rename cannot
 * reintroduce a parent that is also a child.
 *
 * ⚠ «ПЛАТЕЖИ» WAS RENAMED «ИСТОРИЯ ПЛАТЕЖЕЙ» IN THE SAME EDIT, and it is not cosmetic:
 * with a section that TAKES a payment beside it, «Платежи» and «Оплата» in one strip are
 * two words for what a subscriber reads as one thing. The screen's own title has always
 * been «История платежей» (§22); the nav label now says what the screen says.
 *
 * §21's promised-payment FORM has no row of its own: it lives on «Обещанные платежи»,
 * above the history, because the terms, the request and the record of it are one question
 * a subscriber asks once — and a tenth strip entry costs more at 360 px than the grouping
 * saves.
 *
 * ⚠ WHY «ПРОФИЛЬ» SITS FOURTH AND NOT WITH THE OTHER IDENTITY ROWS AT THE END. It is the
 * one W4 section that is not a convenience: `email_confirmed`/`mobile_confirmed` default
 * to FALSE and the notification engine refuses an unconfirmed contact per channel, so
 * until a subscriber has been to this screen «Забыли пароль?» sends him nothing and he
 * receives no suspension notice, no payment confirmation and no dunning warning. At
 * cutover that is EVERY imported subscriber, which makes its expected call volume high
 * and its consequence-of-not-finding-it the highest on the strip — ~~and with fourteen
 * entries the tail of the strip is behind a horizontal scroll on a 360-px handset~~
 * *(struck 2026-08-14: the PREMISE is gone — nothing is behind a horizontal scroll now)*.
 *
 * **⚠ AS BUILT SINCE 2026-08-14 it leads «Настройки» rather than sitting fourth on a strip**,
 * which is the closest a grouped menu comes to honouring the paragraph above: it is the first
 * row of its panel, and the panel is one press from anywhere. That is one press MORE than
 * before and it is a trade taken deliberately — the alternative was to keep it top-level and
 * spend a sixth bar slot on a screen consulted twice a year after cutover. ⚠ **The real repair
 * for the cutover problem is not a menu position at all**: an imported subscriber with no
 * confirmed contact should be TOLD SO on the dashboard he lands on, which no wave has built.
 * Recorded here rather than left as an inference from a nav position.
 *
 * ⚠ AND WHY «ПАРОЛЬ УЧЁТНОЙ ЗАПИСИ» IS ITS OWN ENTRY RATHER THAN A SECOND CARD ON
 * «Изменение пароля». A subscriber holds TWO credentials (P7-A1) and this is exactly the
 * confusion the login screen already carries a hint about; two password forms on ONE page,
 * with one of them asking for «текущий пароль», is an invitation to type the portal
 * password into the Account form and be told it is wrong. Two entries with two unambiguous
 * labels is the cheapest way to keep them apart — and it puts the ONLY self-service route
 * to an Account password (recovery rotates the portal one and never touches this) beside
 * «Мои устройства», which is the other half of what the enrollment portal asks for.
 *
 * ⚠ P2 ADDS FOUR — «Поддержка», «Сообщения», «Цифровое ТВ» and «Оповещения» — and each
 * word left `nav.test.tsx`'s `UNBUILT_VOCABULARY` in the same commit, which is the rule that
 * file states about itself. Their positions:
 *
 *   tickets  — 11th, immediately after the money block. It is the section a subscriber
 *              reaches INSTEAD OF TELEPHONING, which is the whole argument for building it
 *              (F26/D215(b)), so it must not be at the far end of a strip he has to scroll.
 *   news     — 12th, beside it: an outage announcement he has already read is a call he
 *              never makes, and the two «что-то не так» sections belong together.
 *   tv       — 15th, next to «Мои устройства», because both answer «почему не работает» for
 *              one service rather than for the money. Its own commonest question — «где моё
 *              ТВ?» after a new month began — is answered by the state sentence there.
 *   alerts   — 16th, with the settings tail: it is configuration, consulted once.
 *
 * ⚠ AND «ОПОВЕЩЕНИЯ» IS A SEPARATE SECTION FROM «СООБЩЕНИЯ» ON PURPOSE, though the legacy
 * nests both under «Сообщения». They are two different questions — «что мне сообщили» and «о
 * чём мне сообщать» — ~~and this portal has one flat strip with no room for a parent that is
 * not itself a page~~ *(struck 2026-08-14: this portal now HAS parents that are not pages, so
 * that half of the argument is void — the conclusion is unchanged and the two now sit in
 * DIFFERENT groups, «Сообщения» under «Помощь» and «Оповещения» under «Настройки», which says
 * the same thing more loudly than adjacency ever did)*. The §18 CATEGORY CHECKLIST lives on
 * «Оповещения» with the matrix,
 * exactly as §18 draws it, and «Сообщения» carries a sentence pointing at it: the categories
 * decide what arrives, which is a настройка, not a message.
 *
 * ⚠ WHAT §18 DRAWS AND THIS STRIP DOES NOT REPEAT: its own CONTACTS block. It is the same
 * two columns «Профиль» already writes, and a second editor for one pair of columns is two
 * doors to one write — «one door of a pair guarded and its neighbour not» is this project's
 * most repeated review finding. «Оповещения» states the confirmation PRECONDITION and names
 * the section that fixes it.
 *
 * ⚠ ~~Tariff change, statistics and additional services are still unbuilt.~~ **P3 ADDS ALL
 * THREE — the last of Appendix L's self-service sections** — and «Тарифн», «Статистика» and
 * «Дополнительные услуги» left `nav.test.tsx`'s `UNBUILT_VOCABULARY` in the same commit, which
 * is the rule that file states about itself. Their positions, and the argument:
 *
 *   tariff     — 13th, at the HEAD of the service block. It is the section that changes what
 *                he PAYS, so it sits immediately after the two «что-то не так» sections and
 *                before the three that change what he GETS. Its own commonest question —
 *                «когда подешевеет?» — is answered by the boundary date there and nowhere else.
 *   services   — 14th, beside it: the tariff and the extras hanging off it are one question
 *                («что я покупаю у оператора»), and §17's own three lists are reached from the
 *                same «choose the account» step §7 is.
 *   statistics — 17th, between «Мои устройства» and «Цифровое ТВ». All three answer «почему не
 *                работает / сколько я потратил» about the SERVICE rather than about the money,
 *                and statistics is the one of them consulted after a bill rather than before.
 *
 * ⚠ ~~TWENTY-ONE ENTRIES IS THE STRIP'S CEILING AND THIS IS IT.~~ Appendix L specifies ~24
 * sections; the rest are post-launch (F20 template blocking, F25 turbo, F27 knowledge base,
 * F29/F30 promotions) or have no data path at all (telephony statistics), so nothing further is
 * owed here. ~~If another lands, the flat strip has to become something else rather than grow.~~
 *
 * **⚠ 2026-08-14: THIS IS THAT «SOMETHING ELSE», AND IT ARRIVED ONE WAVE LATE.** The ceiling
 * sentence was written as a warning and read as a permission: the strip reached twenty-one and
 * the customer found the horizontal scrollbar before the ceiling did. The lesson is worth more
 * than the rework — **a ceiling that no test can measure is a comment, and a comment does not
 * stop a wave that has a section to ship.** So the ceiling is now a MECHANISM instead of a
 * sentence: `NAV_HOME` below is a `Record<NavKey, …>`, so a twenty-second section does not
 * COMPILE until somebody has decided which group it belongs to. There is no count to exceed,
 * because a group absorbs a row where a strip could only get wider.
 *
 * `nav.test.tsx` fails by name if an entry appears whose screen this app cannot render.
 *
 * ⚠ `ticket` (ONE thread, `/tickets/{id}`) IS NOT A NAV KEY. It is a sub-page of «Поддержка»
 * and `navKeyForRoute` highlights the parent while it is open — the same shape `recover` has,
 * for the opposite reason: `recover` has no authenticated equivalent, and this one has a
 * parent.
 */
export type NavKey = Exclude<RouteKind, 'recover' | 'ticket'>;

export interface NavEntry {
  key: NavKey;
  path: string;
  /** i18n key — never a literal (ST13; no inline strings). */
  labelKey: string;
}

export const NAV_ENTRIES: readonly NavEntry[] = [
  { key: 'home', path: '/', labelKey: 'dashboard.navLabel' },
  { key: 'topup', path: '/topup', labelKey: 'topup.navLabel' },
  { key: 'instructions', path: '/instructions', labelKey: 'instructions.navLabel' },
  { key: 'profile', path: '/profile', labelKey: 'profile.navLabel' },
  { key: 'payments', path: '/payments', labelKey: 'payments.navLabel' },
  { key: 'usage', path: '/usage', labelKey: 'usage.navLabel' },
  { key: 'state', path: '/state', labelKey: 'state.navLabel' },
  { key: 'documents', path: '/documents', labelKey: 'documents.navLabel' },
  { key: 'installments', path: '/installments', labelKey: 'installments.navLabel' },
  { key: 'promises', path: '/promises', labelKey: 'promises.navLabel' },
  { key: 'tickets', path: '/tickets', labelKey: 'tickets.navLabel' },
  { key: 'news', path: '/news', labelKey: 'news.navLabel' },
  { key: 'tariff', path: '/tariff', labelKey: 'tariff.navLabel' },
  { key: 'services', path: '/services', labelKey: 'services.navLabel' },
  { key: 'blockings', path: '/blockings', labelKey: 'blockings.navLabel' },
  { key: 'devices', path: '/devices', labelKey: 'devices.navLabel' },
  { key: 'statistics', path: '/statistics', labelKey: 'statistics.navLabel' },
  { key: 'tv', path: '/tv', labelKey: 'tv.navLabel' },
  { key: 'alerts', path: '/alerts', labelKey: 'alerts.navLabel' },
  { key: 'accountPassword', path: '/account-password', labelKey: 'accountPassword.navLabel' },
  { key: 'password', path: '/password', labelKey: 'password.navLabel' },
];

/**
 * ============================================================================
 * THE GROUPS (2026-08-14 — the rework that removed the horizontal scrollbar)
 * ============================================================================
 *
 * Five groups and one landing link. `NAV_ENTRIES` above stays the SINGLE SOURCE OF TRUTH for
 * what exists, what it is called and where it lives; this block says only where each section
 * HANGS, and the rendered order inside a group is `NAV_ENTRIES`' own order, filtered — never
 * re-sorted. Two tables that could disagree about an order would be two tables to forget.
 */
export type NavGroupKey = 'pay' | 'bill' | 'service' | 'help' | 'settings';

/**
 * ⚠ `Record<NavKey, …>` IS THE POINT OF THIS TABLE, not a type annotation on it. A section
 * added to `NAV_ENTRIES` with no home here is a TypeScript error, so «where does it go?» is
 * answered at compile time by the person who added it and not at review time by somebody
 * counting menu items. `'bar'` means «top level, its own control» and is deliberately spelt
 * out rather than expressed by absence: an omission and a decision look identical in a map,
 * and this project has paid for that difference (D165 — a create-if-absent seeder silently
 * skipping every field added later).
 *
 * ⚠ THE ONE ENTRY AT `'bar'` IS `home`, and it is not a matter of taste. «Мои аккаунты» is
 * the landing page, the page the shell returns to and the page a suspended subscriber is put
 * on; a landing page reached by opening a menu first is a landing page with a toll on it.
 */
export const NAV_HOME: Readonly<Record<NavKey, NavGroupKey | 'bar'>> = {
  home: 'bar',

  // «Платежи» — the money going OUT of his pocket: the form, the alternatives to the form,
  // the deferral when he cannot use either, and the record that it arrived. Its four rows are
  // the spec's own «Payment (Оплата)» subtree minus the two post-launch toggles.
  topup: 'pay',
  instructions: 'pay',
  payments: 'pay',
  promises: 'pay',

  // «Счёт» — the money already spent, and what the operator says about it. The spec's own
  // «Invoice (Счёт)» subtree, exactly.
  state: 'bill',
  usage: 'bill',
  documents: 'bill',
  installments: 'bill',

  // «Услуги» — what he BUYS and whether it is working: the tariff, the extras, the television,
  // the devices that carry it, the traffic it moved and the «отпуск» that pauses it. The spec
  // draws «Интернет» and «Телевидение» separately; here television is one read-only section,
  // and a group of one is a menu item wearing a hat.
  //
  // ⚠ «БЛОКИРОВКИ» IS HERE AND NOT UNDER «СЧЁТ», though the legacy tree nests «Добровольная
  // блокировка» under Invoice. That nesting is about the CHARGE it stops; this build's screen
  // is about the state of the service and the schedule that changes it, and Appendix L's own
  // top-level «Blockings [method A — SHIPS AT LAUNCH]» is the row we actually ship.
  tariff: 'service',
  services: 'service',
  blockings: 'service',
  devices: 'service',
  statistics: 'service',
  tv: 'service',

  // «Помощь» — the two sections a subscriber reaches INSTEAD OF TELEPHONING (F26/D215(b), and
  // an outage announcement already read is a call never made). They are the whole group, which
  // is why the group is two rows deep and near the front rather than five rows deep at the end.
  tickets: 'help',
  news: 'help',

  // «Настройки» — the four screens consulted to CONFIGURE rather than to find something out:
  // his contacts, what he wants to be told about, and his two credentials. ⚠ «Профиль» leads
  // it deliberately — see the block header for why that row is not a convenience, and for the
  // repair that a menu position is not.
  profile: 'settings',
  alerts: 'settings',
  accountPassword: 'settings',
  password: 'settings',
};

/** The order the groups appear in the bar — task order, then configuration last. */
export const NAV_GROUP_ORDER: readonly NavGroupKey[] = ['pay', 'bill', 'service', 'help', 'settings'];

/** i18n keys — never literals (ST13). */
export const NAV_GROUP_LABEL: Readonly<Record<NavGroupKey, string>> = {
  pay: 'nav.groups.pay',
  bill: 'nav.groups.bill',
  service: 'nav.groups.service',
  help: 'nav.groups.help',
  settings: 'nav.groups.settings',
};

export interface NavGroup {
  key: NavGroupKey;
  labelKey: string;
  entries: readonly NavEntry[];
}

/** The top-level entries that are their own control rather than a group member. */
export const NAV_BAR_ENTRIES: readonly NavEntry[] = NAV_ENTRIES.filter(
  (entry) => NAV_HOME[entry.key] === 'bar',
);

export const NAV_GROUPS: readonly NavGroup[] = NAV_GROUP_ORDER.map((key) => ({
  key,
  labelKey: NAV_GROUP_LABEL[key],
  entries: NAV_ENTRIES.filter((entry) => NAV_HOME[entry.key] === key),
}));

/** The group a section hangs under, or `null` when it is its own top-level control. */
export function navGroupForKey(key: NavKey): NavGroupKey | null {
  const home = NAV_HOME[key];
  return home === 'bar' ? null : home;
}

/**
 * navKeyForRoute maps a route to the highlighted entry.
 *
 * `recover` is a PUBLIC screen with no nav entry and no authenticated equivalent, so an
 * authenticated caller who lands on it is shown home — see App.tsx, which also corrects
 * the URL so the address bar and the highlight cannot disagree.
 *
 * `ticket` — ONE thread — has no entry either, and highlights its PARENT «Поддержка». That
 * is not cosmetic: a sub-page with nothing highlighted reads as «I have left the section»,
 * and the section list is the only place this portal tells him where he is.
 *
 * ⚠ SINCE 2026-08-14 THAT HIGHLIGHT HAS TWO LEVELS, and `ticket` exercises both: the answer
 * here (`tickets`) marks the LINK inside its panel, and `navGroupForKey` marks the GROUP
 * button that carries it («Помощь»). The shell also OPENS that group by default, so on
 * `/tickets/12` the subscriber sees «Помощь → Поддержка» highlighted without touching
 * anything — which is what keeps the sentence above true after the strip became a menu.
 */
export function navKeyForRoute(route: Route): NavKey {
  if (route.kind === 'recover') return 'home';
  if (route.kind === 'ticket') return 'tickets';
  return route.kind;
}
